import { PosthogEventType } from "@repo/app-types/client/client";
import { IconAlertTriangle } from "@tabler/icons-react";
import { differenceInCalendarDays } from "date-fns";
import type { FC, ReactNode } from "react";
import { useAppSelector } from "../../hooks/hook.js";
import { captureAnalyticsEvent } from "../analytics/analyticsClient.js";
import { selectCompany } from "../company/companySlice.js";

export const TrialExpirationBanner: FC = () => {
  const company = useAppSelector(selectCompany);

  const trialExpirationDate =
    company?.trial_expires_at && new Date(company.trial_expires_at);

  // Don't render if there's no trial expiration date, the company has an
  // active plan, or it is a demo company shown to prospects.
  if (
    !company ||
    !trialExpirationDate ||
    company.billing_current_plan ||
    company.settings.is_demo
  ) {
    return null;
  }

  const onClickContactUs = () => {
    captureAnalyticsEvent(PosthogEventType.TrialContactUs);
    window.open("mailto:support@alpharun.com", "_blank");
  };

  const contactUsLink = (
    <span className="cursor-pointer font-semibold" onClick={onClickContactUs}>
      Contact us
    </span>
  );

  const isTrialExpired = trialExpirationDate < new Date();

  if (isTrialExpired) {
    return (
      <div className="flex h-9 w-full flex-row items-center justify-center space-x-2 bg-gray-700 text-center text-sm leading-9 text-white">
        <IconAlertTriangle className="h-6 w-6 text-destructive" />
        <span className="truncate">
          {"Your trial has expired, and no new interviews can be conducted. "}
          {contactUsLink}
          {" to upgrade."}
        </span>
      </div>
    );
  }

  const daysRemaining = differenceInCalendarDays(
    trialExpirationDate,
    new Date(),
  );

  const ctaText =
    daysRemaining < 6 ? (
      <>
        {contactUsLink}
        {" to upgrade and prevent blocked interviews as your trial expires."}
      </>
    ) : (
      <>
        {"Questions about Alpharun or ready to upgrade? "}
        {contactUsLink}
        {"."}
      </>
    );

  return (
    <div className="h-9 w-full items-center justify-center bg-gray-700 text-center text-sm leading-9 text-white">
      Trial ends {computeDaysText(daysRemaining)}. {ctaText}
    </div>
  );
};

function computeDaysText(daysRemaining: number): ReactNode {
  switch (daysRemaining) {
    case 0:
      return <span className="font-semibold">today</span>;
    case 1:
      return <span className="font-semibold">tomorrow</span>;
    default:
      return (
        <>
          {"in "}
          <span className="font-semibold">{daysRemaining} days</span>
        </>
      );
  }
}
